import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function LoginPage() {
  const {
    firebaseConfigured,
    loginWithEmail,
    loginWithGoogle,
    loginWithApple,
    sendPasswordReset,
    loading,
    error,
    clearError,
    user,
    firebaseUser,
    authReady,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    if (!authReady || loading) return;
    if (firebaseUser && user) {
      navigate(from, { replace: true });
    }
  }, [authReady, loading, firebaseUser, user, from, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setResetSent(false);
    try {
      await loginWithEmail(email, password);
      /* 遷移はプロフィール取得完了後の useEffect に任せる（早すぎると保護ルートで弾かれる） */
    } catch {
      /* error は AuthContext に設定済み */
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col px-4 py-8"
      style={{
        background: "linear-gradient(160deg, #0d1f1f 0%, #1a2f2f 40%, #0f1a1a 100%)",
        fontFamily: "Noto Sans JP, sans-serif",
      }}
    >
      <div className="max-w-sm mx-auto w-full flex-1 flex flex-col justify-center space-y-6">
        {!firebaseConfigured && (
          <div
            className="rounded-2xl px-4 py-3 text-xs leading-relaxed"
            style={{
              background: "rgba(255,128,128,0.12)",
              border: "1px solid rgba(255,128,128,0.35)",
              color: "#ffb4b4",
            }}
          >
            <p className="font-bold mb-1">Firebase が未設定です</p>
            <p style={{ color: "rgba(255,228,228,0.85)" }}>
              プロジェクト直下に <code className="text-[10px]">.env</code> を作成し、
              <code className="text-[10px]">env.example</code> の <code className="text-[10px]">VITE_FIREBASE_*</code>{" "}
              を埋めたうえで、<code className="text-[10px]">npm run dev</code> を再起動してください。
            </p>
          </div>
        )}

        <div className="text-center">
          <p
            className="text-xs font-bold tracking-widest mb-1"
            style={{ color: "#d4af37", fontFamily: "Montserrat, sans-serif" }}
          >
            Thai Relaxation
          </p>
          <h1
            className="text-2xl font-bold"
            style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}
          >
            BaoBao
          </h1>
          <p className="text-xs mt-2" style={{ color: "rgba(244,229,195,0.5)" }}>
            メールとパスワード、または Google / Apple でログイン
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs mb-1" style={{ color: "rgba(244,229,195,0.6)" }}>
              メールアドレス
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                clearError();
                setResetSent(false);
                setEmail(e.target.value);
              }}
              autoComplete="email"
              required
              className="w-full rounded-xl px-4 py-3 text-base outline-none"
              style={{
                background: "rgba(212,175,55,0.08)",
                border: "1px solid rgba(212,175,55,0.25)",
                color: "#f4e5c3",
              }}
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: "rgba(244,229,195,0.6)" }}>
              パスワード
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                clearError();
                setPassword(e.target.value);
              }}
              autoComplete="current-password"
              required
              className="w-full rounded-xl px-4 py-3 text-base outline-none"
              style={{
                background: "rgba(212,175,55,0.08)",
                border: "1px solid rgba(212,175,55,0.25)",
                color: "#f4e5c3",
              }}
            />
          </div>

          {error && (
            <p className="text-xs text-center" style={{ color: "#ff8080" }}>
              {error}
            </p>
          )}

          {resetSent && !error && (
            <p className="text-xs text-center" style={{ color: "rgba(180,220,180,0.95)" }}>
              再設定用のメールを送信しました。受信トレイをご確認ください。
            </p>
          )}

          <button
            type="button"
            disabled={loading || !firebaseConfigured}
            onClick={async () => {
              setResetSent(false);
              clearError();
              try {
                await sendPasswordReset(email);
                setResetSent(true);
              } catch {
                /* AuthContext */
              }
            }}
            className="w-full text-xs underline cursor-pointer bg-transparent border-0 p-0"
            style={{ color: "rgba(212,175,55,0.75)" }}
          >
            パスワードを忘れた場合（再設定メール）
          </button>

          <button
            type="submit"
            disabled={loading || !firebaseConfigured}
            className="w-full py-3.5 rounded-2xl text-sm font-bold tracking-wider disabled:opacity-50"
            style={{
              background: "linear-gradient(135deg, #b8860b 0%, #d4af37 50%, #b8860b 100%)",
              color: "#1a1a2e",
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            {loading ? "ログイン中…" : "ログイン"}
          </button>
        </form>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.25)" }} />
            <span className="text-xs whitespace-nowrap" style={{ color: "rgba(244,229,195,0.45)" }}>
              または
            </span>
            <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.25)" }} />
          </div>

          <button
            type="button"
            disabled={loading || !firebaseConfigured}
            onClick={async () => {
              clearError();
              try {
                await loginWithGoogle();
              } catch {
                /* AuthContext — Google はリダイレクトで別ページへ遷移します */
              }
            }}
            className="w-full py-3.5 rounded-2xl text-sm font-bold tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
            style={{
              background: "rgba(255,255,255,0.95)",
              color: "#1a1a2e",
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            <span>Google で続ける</span>
          </button>

          <button
            type="button"
            disabled={loading || !firebaseConfigured}
            onClick={async () => {
              clearError();
              try {
                await loginWithApple();
              } catch {
                /* AuthContext — Apple も同タブでリダイレクトします */
              }
            }}
            className="w-full py-3.5 rounded-2xl text-sm font-bold tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
            style={{
              background: "rgba(0,0,0,0.85)",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#f4e5c3",
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            <span>Apple で続ける</span>
          </button>
        </div>

        <p className="text-center text-[10px] leading-relaxed" style={{ color: "rgba(244,229,195,0.35)" }}>
          Google / Apple はポップアップではなく同じタブで認証画面に移動します。Firebase Console で各プロバイダを有効にしてください。
        </p>

        <div className="text-center">
          <Link to="/register" className="text-xs underline" style={{ color: "rgba(212,175,55,0.85)" }}>
            新規会員登録
          </Link>
        </div>
      </div>
    </div>
  );
}
